import React, { useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Sparkles, CheckCircle2 } from 'lucide-react';

/**
 * Lightweight, dependency-free onboarding tour.
 *
 * Each step has:
 *   - title (string)
 *   - body (string)
 *   - target (CSS selector or null for a centered modal)
 *   - placement: 'bottom' | 'right' | 'left' | 'top' | 'center' (default: 'right' for sidebar items)
 *
 * The tour overlays a translucent dim layer, draws a glowing ring around the target,
 * and pops a tooltip card. Completion is remembered via `localStorage[storageKey]='true'`.
 */
const OnboardingTour = ({ steps, storageKey = 'practiq_onboarding_done', onClose, autoStart = true }) => {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!autoStart) return undefined;
    let done = null;
    try { done = localStorage.getItem(storageKey); } catch (_) { /* localStorage may be blocked */ }
    if (done) return undefined;
    // Tiny delay so layout settles before measuring targets
    const t = setTimeout(() => setActive(true), 350);
    return () => clearTimeout(t);
  }, [storageKey, autoStart]);

  const finish = useCallback((markDone = true) => {
    if (markDone) {
      try { localStorage.setItem(storageKey, 'true'); } catch (_) { /* ignore */ }
    }
    setActive(false);
    setStepIdx(0);
    if (onClose) onClose();
  }, [storageKey, onClose]);

  // Compute target rect — runs whenever step or active state changes
  useLayoutEffect(() => {
    if (!active) {
      const c = Promise.resolve().then(() => setRect(null));
      return () => { c.catch(() => {}); };
    }
    const step = steps[stepIdx];
    let cancelled = false;
    const apply = (next) => { if (!cancelled) setRect(next); };
    const compute = () => {
      if (!step || !step.target) { Promise.resolve().then(() => apply(null)); return; }
      const el = document.querySelector(step.target);
      if (!el) { Promise.resolve().then(() => apply(null)); return; }
      const r = el.getBoundingClientRect();
      Promise.resolve().then(() => apply({ top: r.top, left: r.left, width: r.width, height: r.height }));
      if (r.top < 0 || r.bottom > window.innerHeight) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [active, stepIdx, steps]);

  // ESC to close
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === 'Escape') finish(true);
      if (e.key === 'ArrowRight') setStepIdx((i) => Math.min(i + 1, steps.length - 1));
      if (e.key === 'ArrowLeft')  setStepIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, finish, steps.length]);

  if (!active) return null;
  const step = steps[stepIdx];
  if (!step) return null;
  const isLast = stepIdx === steps.length - 1;
  const isFirst = stepIdx === 0;

  // Compute tooltip card position relative to the highlighted rect
  let cardStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  if (rect && step.placement !== 'center') {
    const margin = 16;
    const placement = step.placement || 'right';
    const cardWidth = 360;
    if (placement === 'right') {
      cardStyle = { top: rect.top + rect.height / 2, left: rect.left + rect.width + margin, transform: 'translateY(-50%)' };
      // overflow guard: if not enough room, flip to left
      if (rect.left + rect.width + margin + cardWidth > window.innerWidth - 16) {
        cardStyle = { top: rect.top + rect.height / 2, left: rect.left - margin, transform: 'translate(-100%, -50%)' };
      }
    } else if (placement === 'bottom') {
      cardStyle = { top: rect.top + rect.height + margin, left: rect.left + rect.width / 2, transform: 'translateX(-50%)' };
    } else if (placement === 'top') {
      cardStyle = { top: rect.top - margin, left: rect.left + rect.width / 2, transform: 'translate(-50%, -100%)' };
    } else if (placement === 'left') {
      cardStyle = { top: rect.top + rect.height / 2, left: rect.left - margin, transform: 'translate(-100%, -50%)' };
    }
  }

  return (
    <div className="fixed inset-0 z-[1000]" data-testid="onboarding-tour">
      {/* Dim background — click does nothing (prevents accidental dismiss) */}
      <div className="absolute inset-0 bg-black/65 backdrop-blur-[2px]" />

      {/* Highlight ring around target */}
      {rect && (
        <div
          className="absolute pointer-events-none rounded-xl ring-2 ring-teal-400/90 shadow-[0_0_0_4px_rgba(45,212,191,0.18),0_0_40px_rgba(45,212,191,0.35)] transition-all duration-300"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        className="absolute w-[360px] max-w-[calc(100vw-32px)] bg-[#0F1626] border border-teal-400/30 rounded-2xl shadow-2xl p-5 z-10"
        style={cardStyle}
        data-testid="tour-card"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-400/30 to-cyan-400/30 border border-teal-400/40 flex items-center justify-center">
              {isLast
                ? <CheckCircle2 className="w-4 h-4 text-teal-300" />
                : <Sparkles className="w-4 h-4 text-teal-300" />}
            </div>
            <span className="text-[11px] text-white/50 uppercase tracking-widest">
              Step {stepIdx + 1} of {steps.length}
            </span>
          </div>
          <button
            type="button"
            onClick={() => finish(true)}
            className="text-white/40 hover:text-white p-1"
            aria-label="Skip tour"
            data-testid="tour-skip"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Title + body */}
        <h3 className="text-white text-lg font-semibold mb-1.5">{step.title}</h3>
        <p className="text-white/70 text-sm leading-relaxed">{step.body}</p>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mt-4">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === stepIdx ? 'w-6 bg-teal-400' : 'w-1.5 bg-white/15'
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-2 mt-4">
          <button
            type="button"
            onClick={() => finish(true)}
            className="text-xs text-white/45 hover:text-white"
            data-testid="tour-skip-text"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                type="button"
                onClick={() => setStepIdx((i) => Math.max(i - 1, 0))}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80"
                data-testid="tour-back"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (isLast) finish(true);
                else setStepIdx((i) => Math.min(i + 1, steps.length - 1));
              }}
              className="flex items-center gap-1 px-3.5 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white shadow-lg"
              data-testid={isLast ? 'tour-done' : 'tour-next'}
            >
              {isLast ? 'Get Started' : 'Next'}
              {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
